/** Explicit opt-in for notifications that can arrive after the game tab closes. */
import { josa, withJosa } from '../world/josa.js';
const KEY = 'unige_world_push';
/**
 * UNI-Q187 ③ — the kinds a device can turn on or off. The service worker keeps the choice (it decides what to show)
 * and the server keeps a copy (it never sends a kind the device would hide — Safari revokes a subscription after three
 * pushes without a notification). `KINDS_SYNCED` remembers which endpoint+choice the server already has.
 */
export const WORLD_NOTIFY_KINDS = [
  ['world', '새 세계', '세계가 끝나고 새 세계가 열릴 때'],
  ['daily', '오늘의 목표', '매일 0시(한국 시간) 오늘의 목표가 새로 열릴 때'],
  ['tower', '탑 주간 결과', '월요일 0시 시련의 탑 지난주 순위가 정해졌을 때 — 입상하면 순위와 보상까지'],
  // R217 ⓓ(레드팀 P2-1 ⑤, DESIGN 14.417) — 끝나기 **전**에 한 번. 몇 시간 전인지는 운영 설정(콘솔, 기본 3시간)이다
  ['end', '세계 종료 전', '세계가 끝나기 몇 시간 전 한 번 — 레벨·가방 초기화와 창고 이월 설정을 확인하도록(몇 시간 전인지는 운영 설정)'],
];
const KINDS_SYNCED = 'unige_world_push_kinds';
/**
 * 서비스 워커가 답하지 않은 종류의 값. 앞의 셋은 켬(옛 규칙 그대로), **'end' 는 끔** — 'end' 를 모르는 옛 워커가 서버 사본에 켬으로
 * 올라가면 서버가 보내고 워커는 아무것도 안 띄운다(Safari 는 보이지 않는 알림 셋에 구독을 끊는다 — push-kinds.ts).
 */
const allKinds = () => ({ world: true, daily: true, tower: true, end: false });
let getToken = () => null, changed = () => {};
let config = { enabled: false }, loaded = false, listening = false;
let state = { supported: false, enabled: false, subscribed: false, busy: false, permission: '', message: '알림 설정을 확인 중이다.', kinds: allKinds(), kindsReady: false };
const storage = {
  get() { try { return localStorage.getItem(KEY) === '1'; } catch { return false; } },
  set(on) { try { localStorage.setItem(KEY, on ? '1' : '0'); if (on) localStorage.setItem('unige_notify', '0'); } catch {} },
  synced() { try { return localStorage.getItem(KINDS_SYNCED) || ''; } catch { return ''; } },
  setSynced(mark) { try { localStorage.setItem(KINDS_SYNCED, mark); } catch {} },
};
function report(patch) { state = { ...state, ...patch }; changed(); }
function supported() { try { return isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window; } catch { return false; } }
export const worldNotifyState = () => ({ ...state, kinds: { ...state.kinds } });
async function api(path, body) {
  const response = await fetch('/api/world-notify/' + path, { method: body === undefined ? 'GET' : 'POST',
    headers: { 'content-type': 'application/json', 'x-unige-token': getToken() || '' },
    body: body === undefined ? undefined : JSON.stringify(body), cache: 'no-store' });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || '알림 설정을 저장하지 못했다.');
  return result;
}
function matchingKey(sub) {
  const key = sub.options?.applicationServerKey;
  if (!key) return true;
  const a = new Uint8Array(key), b = bytes(config.publicKey);
  return a.length === b.length && a.every((v, i) => v === b[i]);
}
function bytes(s) { return Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0)); }
async function registration() {
  await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
  let timer;
  try { return await Promise.race([navigator.serviceWorker.ready, new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('알림을 준비하지 못했다. 잠시 뒤 다시 시도해 달라.')), 15000); })]); }
  finally { clearTimeout(timer); }
}
/** One question to the active service worker; resolves with its answer. A worker that does not answer times out. */
async function workerAsk(reg, message, failure) {
  if (!reg.active) throw new Error('알림 서비스가 아직 준비되지 않았다.');
  const channel = new MessageChannel(); let timer;
  try {
    return await new Promise((resolve, reject) => {
      timer = setTimeout(() => reject(new Error(failure)), 5000);
      channel.port1.onmessage = (e) => e.data?.ok ? resolve(e.data) : reject(new Error(failure));
      reg.active.postMessage(message, [channel.port2]);
    });
  } finally { clearTimeout(timer); channel.port1.close(); channel.port2.close(); }
}
const workerSetting = (reg, enabled) => workerAsk(reg, { t: 'world-notify-enabled', enabled }, '알림 설정을 이 기기에 저장하지 못했다.');
/** Reads (and, given `kinds`, writes) this device's kinds. A service worker from before UNI-Q187 ③ never answers. */
async function workerKinds(reg, kinds) {
  const reply = await workerAsk(reg, kinds ? { t: 'world-notify-kinds', kinds } : { t: 'world-notify-kinds' }, '알림 종류를 이 기기에서 확인하지 못했다.');
  if (!reply.kinds || typeof reply.kinds !== 'object') throw new Error('알림 종류를 이 기기에서 확인하지 못했다.');
  return { ...allKinds(), ...reply.kinds };
}
/** Endpoint + choice + day: the server copy is re-sent at most once a day anyway, so a copy the server lost heals on the next visit. */
const kindsMark = (sub, kinds) => sub.endpoint + '|' + WORLD_NOTIFY_KINDS.map(([kind]) => (kinds[kind] ? 1 : 0)).join('') + '|' + new Date().toISOString().slice(0, 10);
/** The server copy. Skipped when this endpoint+choice was already stored today, unless `force` (a new or repaired subscription). */
async function syncKinds(sub, kinds, force) {
  const mark = kindsMark(sub, kinds);
  if (!force && storage.synced() === mark) return;
  await api('kinds', { endpoint: sub.endpoint, kinds });
  storage.setSynced(mark);
}
/**
 * After a subscription is stored: learn this device's kinds and give the server its copy.
 * 'device' = the service worker is older than the kinds (the server keeps sending only 새 세계 to it) ·
 * 'server' = the copy was not stored (the next state check tries again) · 'ok'.
 */
async function adoptKinds(reg, sub, force) {
  let kinds;
  try { kinds = await workerKinds(reg); } catch { report({ kindsReady: false }); return 'device'; }
  report({ kinds, kindsReady: true });
  try { await syncKinds(sub, kinds, force); } catch { return 'server'; }
  return 'ok';
}
const KINDS_PENDING = {
  device: ' 오늘의 목표·탑 주간 결과·세계 종료 전은 게임을 한 번 새로 연 뒤 고를 수 있다.',
  server: ' 알림 종류를 서버에 저장하지 못했다 — 「상태 확인」을 누르면 다시 시도한다.',
  ok: '',
};
export function initWorldNotify(options) {
  getToken = options.token; changed = options.changed || (() => {});
  if (!listening && supported()) {
    navigator.serviceWorker.addEventListener('message', (e) => { if (e.data?.t === 'world-notify-renew') void refreshWorldNotify(); });
    // After a deploy the page may first reach the old service worker, which cannot answer the kind question.
    // Ask again once the new one takes over this page, so the server learns this device's kinds without a reload.
    navigator.serviceWorker.addEventListener('controllerchange', () => { if (state.subscribed && !state.kindsReady) void refreshWorldNotify(); });
    listening = true;
  }
  return refreshWorldNotify();
}
/** Reads state and renews an existing explicit subscription; never asks permission. */
export async function refreshWorldNotify() {
  const available = supported(); report({ supported: available });
  if (!available) { report({ message: '이 브라우저는 닫힌 탭 알림을 지원하지 않는다. iPhone·iPad는 홈 화면에 설치한 뒤 확인해 달라.' }); return; }
  try {
    config = await api('config'); loaded = true;
    const permission = Notification.permission;
    const reg = await navigator.serviceWorker.getRegistration('/'), sub = await reg?.pushManager.getSubscription();
    let subscribed = false, repaired = false;
    if (sub && config.enabled && !matchingKey(sub)) { report({ enabled: true, subscribed: false, permission, message: '알림 연결을 갱신해야 한다. 알림을 다시 켜 달라.' }); return; }
    if (sub && getToken()) {
      const status = await api('status', { endpoint: sub.endpoint }); subscribed = status.subscribed;
      if (!subscribed && storage.get() && permission === 'granted' && config.enabled && !status.unavailable) {
        await workerSetting(reg, true); await api('subscribe', sub.toJSON()); subscribed = true; repaired = true;
      }
    }
    const kinds = subscribed && config.enabled ? await adoptKinds(reg, sub, repaired) : null;
    report({ enabled: !!config.enabled, permission, subscribed, ...(kinds ? {} : { kindsReady: false }), message: !config.enabled
      ? '탭을 닫은 뒤 받는 알림은 준비 중이다.' : subscribed ? '이 기기에 닫힌 탭 알림이 켜져 있다.' + KINDS_PENDING[kinds]
        : permission === 'denied' ? '브라우저 설정에서 유니지 알림 권한을 허용해 달라.' : `알림을 켜면 ${withJosa(WORLD_NOTIFY_KINDS.map(([, name]) => name).join('·'), '을')} 이 기기로 알려준다.` });
  } catch (error) { report({ message: error.message || '알림 상태를 확인하지 못했다.' }); }
}
export async function enableWorldNotify() {
  if (state.busy) return;
  if (!loaded || !config.enabled || !supported()) { await refreshWorldNotify(); return; }
  if (!getToken()) { report({ message: '게임 계정을 불러온 뒤 알림을 켤 수 있다.' }); return; }
  report({ busy: true }); let created = null;
  try {
    // This call stays in the user's click handler, before any network await.
    const permission = Notification.permission === 'default' ? await Notification.requestPermission() : Notification.permission;
    if (permission !== 'granted') { report({ permission, message: '알림을 허용하지 않았다. 게임은 그대로 계속할 수 있다.' }); return; }
    const reg = await registration();
    let sub = await reg.pushManager.getSubscription();
    if (sub && !matchingKey(sub)) { await api('unsubscribe', { endpoint: sub.endpoint }); await sub.unsubscribe(); sub = null; }
    if (!sub) { sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: bytes(config.publicKey) }); created = sub; }
    await workerSetting(reg, true);
    await api('subscribe', sub.toJSON());
    storage.set(true);
    const kinds = await adoptKinds(reg, sub, true);
    report({ subscribed: true, permission, message: '알림을 켰다. 탭을 닫아도 브라우저·기기 설정이 허용하면 도착한다.'
      + (kinds === 'ok' ? ' 받을 종류는 아래에서 고른다.' : KINDS_PENDING[kinds]) });
  } catch (error) {
    if (created) await created.unsubscribe().catch(() => {});
    report({ message: error.message || '알림을 켜지 못했다. 다시 시도해 달라.' });
  } finally { report({ busy: false }); }
}
/**
 * One kind on or off. Turning on: the device learns first, then the server sends. Turning off: the server stops
 * first, then the device. A failure in the second step undoes the first, so the server never sends what this device hides.
 */
export async function setWorldNotifyKind(kind, on) {
  if (state.busy || !state.subscribed || !state.kindsReady || !WORLD_NOTIFY_KINDS.some(([k]) => k === kind)) return;
  report({ busy: true });
  const before = { ...state.kinds }, next = { ...before, [kind]: on === true };
  const name = WORLD_NOTIFY_KINDS.find(([k]) => k === kind)[1];
  try {
    const reg = await navigator.serviceWorker.getRegistration('/'), sub = await reg?.pushManager.getSubscription();
    if (!reg || !sub) throw new Error('이 기기의 알림 구독을 찾지 못했다. 알림을 다시 켜 달라.');
    if (on === true) {
      // R217 ⓓ — 이 종류를 모르는 옛 서비스 워커는 켠 값을 저장하지 못한다(답에 없다). 서버에 켬을 올리기 전에 멈춘다
      const stored = await workerKinds(reg, next);
      if (stored[kind] !== true) throw new Error(`이 기기의 알림 서비스가 아직 「${name}」${josa(name, '을')} 모른다 — 게임을 한 번 새로 연 뒤 다시 켜 달라.`);
      try { await syncKinds(sub, next, true); } catch (error) { await workerKinds(reg, before).catch(() => {}); throw error; }
    } else {
      await syncKinds(sub, next, true);
      try { await workerKinds(reg, next); } catch (error) { await syncKinds(sub, before, true).catch(() => {}); throw error; }
    }
    report({ kinds: next, message: `${name} 알림을 ${on === true ? '켰다' : '껐다'}.` });
  } catch (error) { report({ message: error.message || '알림 종류를 바꾸지 못했다. 다시 시도해 달라.' }); }
  finally { report({ busy: false }); }
}
export async function disableWorldNotify() {
  if (state.busy) return;
  report({ busy: true });
  try {
    const reg = await navigator.serviceWorker.getRegistration('/'), sub = await reg?.pushManager.getSubscription();
    if (reg) await workerSetting(reg, false);
    storage.set(false); storage.setSynced('');
    let server = true;
    if (sub) {
      try { await api('unsubscribe', { endpoint: sub.endpoint }); } catch { server = false; }
      const removed = await sub.unsubscribe();
      if (!removed && await reg.pushManager.getSubscription()) throw new Error('알림 해제를 확인하지 못했다. 알림 끄기를 다시 눌러 달라.');
    }
    report({ subscribed: false, kindsReady: false, message: server ? '이 기기의 닫힌 탭 알림을 껐다.' : '이 기기의 닫힌 탭 알림을 껐다. 이 기기에는 알림이 표시되지 않는다.' });
  } catch (error) { report({ message: error.message || '알림을 끄지 못했다. 다시 시도해 달라.' }); }
  finally { report({ busy: false }); }
}
